import { ButtonMenu, useModal } from "@homebound/beam";
import { ReadyPlanLikeButtonMenuFragment } from "src/components/ReadyPlanButtonMenu.generated";
import { ReadyPlanStatus } from "src/generated/graphqlTypes";
import { useRouteParams } from "src/hooks/useRouteParams";
import { ConfirmationModal } from "src/routes/components/ConfirmationModal";
import { disableBasedOnPotentialOperation } from "src/routes/components/PotentialOperationsUtils";
import { DesignPackagePruneSlotsModal } from "src/routes/libraries/design-catalog/design-package-configurator/components/DesignPackagePruneSlotsModal";
import { DesignCatalogsTableDocument } from "src/routes/libraries/design-catalog/DesignCatalogsPage.generated";
import { useConfirmDiscardDraftVersionModalButtonMenuItem } from "src/routes/libraries/plan-package-v2/components/ConfirmDiscardDraftVersionModal";
import {
  createDesignPackagePersonalizationGroupsUrl,
  createDesignPackageRequiredOptionsUrl,
  createDesignPackageUrl,
  createDesignPackageVersionHistoryUrl,
} from "src/RouteUrls";
import { CopyDesignPackageModal } from "../../CopyDesignPackageModal";
import { useSaveDesignPackageMutation } from "./DesignPackageButtonMenu.generated";

type DesignPackageButtonMenuProps = { designPackage: ReadyPlanLikeButtonMenuFragment };

export function DesignPackageButtonMenu({ designPackage }: DesignPackageButtonMenuProps) {
  const { designPackageVersionId = designPackage.aggregateLatest.id } = useRouteParams<{
    designPackageVersionId: string;
  }>();
  const discardDraftMenuItem = useConfirmDiscardDraftVersionModalButtonMenuItem(designPackage, createDesignPackageUrl);
  const { openModal } = useModal();
  const [saveDesignPackage] = useSaveDesignPackageMutation({ refetchQueries: [DesignCatalogsTableDocument] });
  const isArchived = designPackage.status.code === ReadyPlanStatus.Archived;

  return (
    <ButtonMenu
      trigger={{ icon: "verticalDots" }}
      items={[
        ...(designPackageVersionId
          ? [
              {
                label: `${designPackage.canEdit.allowed ? "Edit" : "View"} Required Add-Ons`,
                onClick: createDesignPackageRequiredOptionsUrl(designPackage.id, designPackageVersionId),
              },
            ]
          : []),
        {
          label: "View Personalization Groups",
          onClick: createDesignPackagePersonalizationGroupsUrl(designPackage.id, designPackageVersionId),
        },
        {
          label: "Version History",
          onClick: createDesignPackageVersionHistoryUrl(designPackage.id, designPackageVersionId),
        },
        discardDraftMenuItem,
        {
          label: "Make a Copy",
          onClick: () => openModal({ content: <CopyDesignPackageModal id={designPackage.id} />, size: "lg" }),
        },
        {
          label: "Prune Unused Slots",
          onClick: () =>
            openModal({
              content: (
                <DesignPackagePruneSlotsModal designPackageId={designPackage.id} versionId={designPackageVersionId} />
              ),
              size: "lg",
            }),
          disabled: disableBasedOnPotentialOperation(designPackage.canEdit),
        },
        isArchived
          ? {
              label: "Unarchive",
              onClick: () =>
                saveDesignPackage({ variables: { input: { id: designPackage.id, status: ReadyPlanStatus.Active } } }),
            }
          : {
              label: "Archive",
              onClick: () =>
                openModal({
                  content: (
                    <ConfirmationModal
                      title="Archive Design Package"
                      label="Archive"
                      danger
                      onConfirmAction={() =>
                        saveDesignPackage({
                          variables: { input: { id: designPackage.id, status: ReadyPlanStatus.Archived } },
                        })
                      }
                      confirmationMessage="Once archived, this design package will not be available for creating Product Offerings."
                    />
                  ),
                }),
            },
      ]}
    />
  );
}
